import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, ShieldCheck, ShieldOff, AlertCircle, Copy, Check, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import NotificationSettings from '../components/NotificationSettings';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecuritySettings() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [disableMode, setDisableMode] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [password, setPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetch2FAStatus();
  }, []);

  const fetch2FAStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFAEnabled(response.data.totp_enabled);
    } catch (error) {
      console.error('Failed to fetch 2FA status', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    if (!password) {
      toast.error('Veuillez entrer votre mot de passe');
      return;
    }

    setActionLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/2fa/setup`, 
        { password },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setQrCode(response.data.qr_code);
      setManualSecret(response.data.secret);
      setSetupMode(true);
      setPassword('');
      toast.success('QR Code généré! Scannez-le avec votre application');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la configuration');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/2fa/verify`, 
        { code: verifyCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setTwoFAEnabled(true);
      setSetupMode(false);
      setQrCode('');
      setManualSecret('');
      setVerifyCode('');
      toast.success('Authentification 2FA activée!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!password || verifyCode.length !== 6) {
      toast.error('Veuillez entrer votre mot de passe et le code 2FA');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/2fa/disable`, 
        { password, code: verifyCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setTwoFAEnabled(false);
      setDisableMode(false);
      setPassword('');
      setVerifyCode('');
      toast.success('Authentification 2FA désactivée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la désactivation');
    } finally {
      setActionLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(manualSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Clé copiée!');
  };

  const cancelSetup = () => {
    setSetupMode(false);
    setQrCode('');
    setManualSecret('');
    setVerifyCode('');
    setPassword('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Paramètres de sécurité</h1>
        <p className="text-slate-600">Gérez l'authentification à deux facteurs de votre compte</p>
      </div>

      {/* 2FA Status Card */}
      <Card className={twoFAEnabled ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
        <CardHeader>
          <div className="flex items-center gap-3">
            {twoFAEnabled ? (
              <ShieldCheck className="w-8 h-8 text-green-600" />
            ) : (
              <ShieldOff className="w-8 h-8 text-amber-600" />
            )}
            <div>
              <CardTitle className="text-lg">
                Authentification à deux facteurs (2FA)
              </CardTitle>
              <CardDescription>
                {twoFAEnabled 
                  ? 'Votre compte est protégé par l\'authentification 2FA'
                  : 'Ajoutez une couche de sécurité supplémentaire à votre compte'
                }
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {twoFAEnabled ? (
            <div className="flex items-center justify-between">
              <span className="text-green-700 font-medium">Activé</span>
              {!disableMode && (
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setDisableMode(true)}
                >
                  Désactiver
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-amber-700 font-medium">Non activé</span>
              {!setupMode && (
                <Button 
                  className="bg-primary hover:bg-primary-700"
                  onClick={() => setSetupMode(true)}
                >
                  Activer la 2FA
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Mode */}
      {setupMode && !twoFAEnabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Configuration de la 2FA
            </CardTitle>
            <CardDescription>
              Suivez les étapes pour activer l'authentification à deux facteurs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {!qrCode ? (
              /* Step 1: Enter password */
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Étape 1:</strong> Confirmez votre identité en entrant votre mot de passe actuel
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe actuel</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pr-10"
                      data-testid="2fa-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSetup2FA} 
                    disabled={!password || actionLoading}
                    className="bg-primary hover:bg-primary-700"
                    data-testid="generate-qr-button"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Générer le QR Code
                  </Button>
                  <Button variant="outline" onClick={cancelSetup}>
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              /* Step 2: Scan QR and verify */
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Étape 2:</strong> Scannez le QR code avec une application d'authentification 
                    (Google Authenticator, Authy, etc.)
                  </p>
                </div>
                
                {/* QR Code */}
                <div className="flex flex-col items-center gap-4">
                  <div className="p-4 bg-white rounded-lg border shadow-sm">
                    <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48" />
                  </div>
                  
                  {/* Manual entry */}
                  <div className="w-full space-y-2">
                    <Label className="text-sm text-slate-600">Ou entrez cette clé manuellement:</Label>
                    <div className="flex gap-2">
                      <Input 
                        value={manualSecret} 
                        readOnly 
                        className="font-mono text-sm bg-slate-50"
                      />
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={copySecret}
                        data-testid="copy-secret-button"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Verify code */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    <strong>Étape 3:</strong> Entrez le code à 6 chiffres affiché dans votre application
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Code de vérification</Label>
                  <Input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest"
                    maxLength={6}
                    data-testid="verify-code-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleVerify2FA} 
                    disabled={verifyCode.length !== 6 || actionLoading}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="enable-2fa-button"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Activer la 2FA
                  </Button>
                  <Button variant="outline" onClick={cancelSetup}>
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Disable Mode */}
      {disableMode && twoFAEnabled && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              Désactiver la 2FA
            </CardTitle>
            <CardDescription>
              Attention: Désactiver la 2FA rendra votre compte moins sécurisé
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Mot de passe actuel</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                  data-testid="disable-2fa-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Code 2FA actuel</Label>
              <Input
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-xl tracking-widest"
                maxLength={6}
                data-testid="disable-2fa-code"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleDisable2FA} 
                disabled={!password || verifyCode.length !== 6 || actionLoading}
                variant="destructive"
                data-testid="confirm-disable-2fa"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Désactiver
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setDisableMode(false);
                  setPassword('');
                  setVerifyCode('');
                }}
              >
                Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-slate-600 mt-0.5" />
            <div className="space-y-2 text-sm text-slate-600">
              <p className="font-medium text-slate-700">À propos de l'authentification 2FA</p>
              <p>
                L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte.
                En plus de votre mot de passe, vous devrez entrer un code temporaire généré par votre application
                d'authentification à chaque connexion.
              </p>
              <p>
                <strong>Applications recommandées:</strong> Google Authenticator, Authy, Microsoft Authenticator
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <div className="border-t pt-6 mt-6">
        <NotificationSettings />
      </div>
    </div>
  );
}
